import React, { useState, useEffect } from "react";
import { IMaskInput } from "react-imask";
import { useAuth } from "../../context/AuthContext.tsx";
import api from "../../api.ts";
import Menu from "../../components/layout/Menu.tsx";
import Footer from "../../components/layout/Footer.tsx";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ui/ConfirmationModal.tsx";
import LoadingSpinner from "../../components/ui/LoadingSpinner.tsx";
import CertificadosExternos from "../../components/profile/CertificadosExternos.tsx";
import CertificadosCursos from "../../components/profile/CertificadosCursos.tsx";
import AvatarModal from "../../components/forms/AvatarModal.tsx";
import PraiseCard, { Praise } from "../../components/praises/PraiseCard.tsx";
import {
  mesCurto,
  mesTitulo,
} from "../../components/praises/praiseMonths.ts";
import {
  dataBR,
  iconeDoTipo,
  nomeDoAtivo,
  rotuloNumeroSerie,
  AssetCategoria,
} from "../../components/itAssets/assetConfig.ts";
import { HiOutlineUserCircle } from "react-icons/hi";
import { FiEdit, FiEye, FiCamera, FiLogOut } from "react-icons/fi";
import { PiPencilSimpleLineBold } from "react-icons/pi";

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface MyAsset {
  id: number;
  tipo_nome: string;
  categoria: AssetCategoria;
  patrimonio: string | null;
  marca: string | null;
  modelo: string | null;
  numero_serie: string | null;
  responsavel_desde: string | null;
}

interface User {
  id: number;
  email: string;
  nome: string;
  role: "admin" | "licenciado" | "colaborador";
  avatar_url?: string;
  cargo?: string;
  setor?: string;
  unit_id?: number;
  unidade_id?: number;
  unidade?: string;
  telefone?: string;
  data_admissao?: string;
  nickname?: string;
}

// ─── Helper de formatação de telefone ────────────────────────────────────────

const formatarTelefone = (telefone?: string | null): string => {
  if (!telefone) return "Não informado";
  const digitos = telefone.replace(/\D/g, "");
  if (digitos.length !== 11) return telefone;
  return `(${digitos.substring(0, 2)}) ${digitos.substring(2, 7)}-${digitos.substring(7)}`;
};

// ─── Componente ───────────────────────────────────────────────────────────────

const Perfil: React.FC = () => {
  const { user, login, logout, loading, hasPermission } = useAuth() as {
    user: User | null;
    login: (u: any, t?: string) => void;
    logout: () => void;
    loading: boolean;
    hasPermission: (key: string) => boolean;
  };
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<"info" | "certificates">("info");
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [myPraises, setMyPraises] = useState<Praise[]>([]);
  const [myAssets, setMyAssets] = useState<MyAsset[]>([]);
  // Mês em foco nos elogios (null = o mais recente) e se a lista do mês está
  // aberta além dos primeiros.
  const [praiseMonth, setPraiseMonth] = useState<string | null>(null);
  const [showAllPraises, setShowAllPraises] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    nome: "",
    cargo: "",
    setor: "",
    telefone: "",
    nickname: "",
  });

  // ─── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!loading && user) {
      setEditForm({
        nome: user.nome || "",
        cargo: user.cargo || "",
        setor: user.setor || "",
        telefone: user.telefone || "",
        nickname: user.nickname || "",
      });
    }
  }, [user, loading]);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/");
    }
  }, [user, loading, navigate]);

  // Elogios recebidos (pessoa ou setor) — somente colaboradores internos.
  useEffect(() => {
    if (!user || !hasPermission("internal_access")) return;
    api
      .get("/api/praises/mine")
      .then((res) => setMyPraises(res.data as Praise[]))
      .catch(() => setMyPraises([]));
  }, [user, hasPermission]);

  // Equipamentos do Inventário de TI sob a responsabilidade do colaborador.
  useEffect(() => {
    if (!user || !hasPermission("internal_access")) return;
    api
      .get("/api/it-assets/mine")
      .then((res) => setMyAssets(res.data as MyAsset[]))
      .catch(() => setMyAssets([]));
  }, [user, hasPermission]);

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const handleSaveChanges = async () => {
    if (!user) return;

    // Não-internos não editam o nome (não há campo na UI) → mantém o atual.
    const nomeAtualizado = hasPermission("internal_access")
      ? editForm.nome
      : user.nome || "";

    if (!nomeAtualizado.trim()) {
      toast.error("O nome não pode estar vazio.");
      return;
    }

    const payload: any = {
      ...user,
      nome: nomeAtualizado,
      data_admissao: user.data_admissao,
      nickname: editForm.nickname.trim() || null, // ADICIONADO
    };

    if (hasPermission("internal_access")) {
      payload.cargo = editForm.cargo;
      payload.setor = editForm.setor;
      payload.telefone = editForm.telefone.replace(/\D/g, "");
    }

    try {
      const res = await api.put(`/api/users/admin/${user.id}`, payload);
      login(res.data.user, res.data.token);
      toast.success("Perfil atualizado com sucesso!");
      setIsEditing(false);
    } catch (err: any) {
      const msg = err.response?.data?.error || "Erro ao salvar as alterações.";
      console.error("Erro 400 Detalhado:", msg);
      toast.error(msg);
    }
  };

  const handleChangePassword = async () => {
    if (!user) return;

    if (newPassword !== confirmPassword) {
      toast.error("A nova senha e a confirmação não correspondem.");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("A nova senha deve ter pelo menos 6 caracteres.");
      return;
    }

    try {
      await api.put(`/api/users/${user.id}/change-password`, {
        currentPassword,
        newPassword,
      });
      toast.success("Senha alterada com sucesso!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || "Erro ao alterar a senha.";
      toast.error(errorMessage);
      console.error(err);
    }
  };

  const handleConfirmRemoveAvatar = async () => {
    if (!user) return;
    try {
      const res = await api.delete(`/api/users/${user.id}/avatar`);
      login(res.data.user, res.data.token);
      toast.success("Foto de perfil removida com sucesso!");
    } catch (err) {
      toast.error("Erro ao remover a foto.");
      console.error(err);
    } finally {
      setIsConfirmModalOpen(false);
    }
  };

  // ─── Guards de render ─────────────────────────────────────────────────────

  // Quantos elogios do mês aparecem antes do "Ver mais".
  const PRAISES_VISIVEIS = 3;

  // Meses com elogios, do mais recente ao mais antigo (a lista já vem
  // ordenada por competência do backend; aqui só tiramos as repetições).
  const praiseMonths = myPraises.reduce<string[]>((acc, p) => {
    const mes = p.reference_month || "";
    if (mes && !acc.includes(mes)) acc.push(mes);
    return acc;
  }, []);

  const praiseMonthAtivo = praiseMonth || praiseMonths[0] || null;

  const praisesDoMes = myPraises.filter(
    (p) => !praiseMonthAtivo || p.reference_month === praiseMonthAtivo,
  );

  const praisesVisiveis = showAllPraises
    ? praisesDoMes
    : praisesDoMes.slice(0, PRAISES_VISIVEIS);

  const praisesRestantes = praisesDoMes.length - praisesVisiveis.length;

  const trocarMesElogios = (mes: string) => {
    setPraiseMonth(mes);
    // Mês novo começa recolhido, senão a lista voltaria a ser longa.
    setShowAllPraises(false);
  };

  if (loading) return <LoadingSpinner />;
  if (!user) return null;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="p-2">
      <Menu />
      <div className="content-area">
        {/* ── Cabeçalho do perfil ── */}
        <div className="profile-header">
          <div className="profile-avatar-container">
            {user.avatar_url ? (
              <img
                src={user.avatar_url}
                alt="Foto de Perfil"
                className="profile-avatar-main"
              />
            ) : (
              <HiOutlineUserCircle className="profile-avatar-main" />
            )}
            <button
              className="form-icon-photo camera"
              onClick={() => setIsAvatarModalOpen(true)}
            >
              <FiCamera />
            </button>
          </div>

          <div className="profile-actions">
            <div className="profile-header-info">
              <h2>{user.nome}</h2>
              <p>{user.email}</p>
            </div>
            <button
              className="form-icon-delete"
              title="Desconectar"
              aria-label="Desconectar"
              onClick={() => {
                logout();
                navigate("/");
              }}
            >
              <FiLogOut /> Desconectar
            </button>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="tabs">
          <button
            className={`tab-item ${activeTab === "info" ? "active" : ""}`}
            onClick={() => setActiveTab("info")}
          >
            Informações Gerais
          </button>
          <button
            className={`tab-item ${activeTab === "certificates" ? "active" : ""}`}
            onClick={() => setActiveTab("certificates")}
          >
            Meus Certificados
          </button>
        </div>

        {/* ─────────────────────────────────────────────────────────────────── */}
        {/* TAB: Informações Gerais                                             */}
        {/* ─────────────────────────────────────────────────────────────────── */}
        {activeTab === "info" && (
          <div className="profile-tab-content">
            {/* ── Informações do Perfil ── */}
            <div className="profile-section">
              <h3>
                Informações do Perfil
                {!isEditing && (
                  <button
                    className="form-icon-edit"
                    onClick={() => setIsEditing(true)}
                  >
                    <FiEdit /> Editar
                  </button>
                )}
              </h3>

              {!isEditing && (
                <div className="profile-info-grid">
                  <div className="info-item">
                    <span>Apelido</span>
                    <p>{user.nickname}</p>
                  </div>
                  <div className="info-item">
                    <span>Email</span>
                    <p>{user.email}</p>
                  </div>
                  {hasPermission("internal_access") && (
                    <div className="info-item">
                      <span>Telefone</span>
                      <p>{formatarTelefone(user.telefone)}</p>
                    </div>
                  )}
                  {hasPermission("internal_access") && (
                    <div className="info-item">
                      <span>Cargo</span>
                      <p>{user.cargo || "Não informado"}</p>
                    </div>
                  )}
                  {hasPermission("internal_access") && (
                    <div className="info-item">
                      <span>Setor</span>
                      <p>{user.setor || "Não informado"}</p>
                    </div>
                  )}
                  
                </div>
              )}

              {isEditing && (
                <div className="profile-edit-form">
                  <div className="form-row">
                    <label>Apelido:</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editForm.nickname}
                      onChange={(e) =>
                        setEditForm({ ...editForm, nickname: e.target.value })
                      }
                    />
                  </div>

                  {hasPermission("internal_access") && (
                    <div className="form-row">
                      <label>Telefone:</label>
                      <IMaskInput
                        mask="(00) 00000-0000"
                        value={editForm.telefone}
                        onAccept={(value: any) =>
                          setEditForm({ ...editForm, telefone: value })
                        }
                        type="tel"
                        className="form-input"
                        placeholder="(00) 00000-0000"
                      />
                    </div>
                  )}

                  <div className="form-actions">
                    <button
                      className="form-button-cancel"
                      onClick={() => setIsEditing(false)}
                    >
                      Cancelar
                    </button>
                    <button className="form-button" onClick={handleSaveChanges}>
                      Salvar Alterações
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* ── Seus Elogios ── */}
            {hasPermission("internal_access") && myPraises.length > 0 && (
              <div className="profile-section">
                <h3>Seus Elogios</h3>
                <p>
                  Reconhecimentos recebidos por você ou pelo seu setor. Quem
                  escreveu permanece anônimo. 💛
                </p>
                {praiseMonths.length > 1 && (
                  <div
                    className="praise-month-tabs"
                    role="tablist"
                    aria-label="Mês dos elogios"
                  >
                    {praiseMonths.map((mes) => (
                      <button
                        key={mes}
                        type="button"
                        role="tab"
                        aria-selected={mes === praiseMonthAtivo}
                        className={`praise-month-tab${
                          mes === praiseMonthAtivo
                            ? " praise-month-tab--active"
                            : ""
                        }`}
                        onClick={() => trocarMesElogios(mes)}
                      >
                        <span className="praise-month-tab-label">
                          {mesCurto(mes)}
                        </span>
                        <span className="praise-month-tab-count">
                          {
                            myPraises.filter((p) => p.reference_month === mes)
                              .length
                          }
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                <div className="praise-month-summary">
                  <h4 className="praise-month-title">
                    {mesTitulo(praiseMonthAtivo)}
                  </h4>
                  <span className="praise-month-meta">
                    {praisesDoMes.length} elogio
                    {praisesDoMes.length === 1 ? "" : "s"}
                  </span>
                </div>

                <div className="praise-grid praise-grid--testimonial">
                  {praisesVisiveis.map((p) => (
                    <PraiseCard
                      key={p.id}
                      praise={p}
                      showRecipient={false}
                      variant="testimonial"
                    />
                  ))}
                </div>

                {(praisesRestantes > 0 || showAllPraises) && (
                  <div className="praise-more-row">
                    <button
                      type="button"
                      className="list-button"
                      onClick={() => setShowAllPraises((v) => !v)}
                    >
                      {showAllPraises
                        ? "Ver menos"
                        : `Ver mais (${praisesRestantes})`}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── Meus equipamentos ── */}
            {myAssets.length > 0 && (
              <div className="profile-section">
                <h3>Meus Equipamentos</h3>
                <p>
                  Equipamentos de TI sob a sua responsabilidade. Se algo estiver
                  diferente, fale com o time de TI.
                </p>
                <ul className="inv-mine">
                  {myAssets.map((a) => {
                    const Icone = iconeDoTipo(a.categoria);
                    return (
                      <li key={a.id} className="inv-mine-item">
                        <span className="inv-detail-icon" aria-hidden="true">
                          <Icone />
                        </span>
                        <div>
                          <p className="inv-mine-name">{nomeDoAtivo(a)}</p>
                          <p className="inv-mine-meta">
                            {a.patrimonio
                              ? `Patrimônio ${a.patrimonio}`
                              : a.numero_serie
                                ? `${rotuloNumeroSerie(a.categoria)} ${a.numero_serie}`
                                : a.tipo_nome}
                            {a.responsavel_desde &&
                              ` · desde ${dataBR(a.responsavel_desde)}`}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* ── Perfil Comportamental ── */}
            {hasPermission("internal_access") && (
              <div className="profile-section">
                <h3>Perfil Comportamental</h3>
                <p>
                  Realize este teste rápido e entenda melhor seus traços de
                  personalidade e como você interage em equipe.
                </p>
                <div className="profile-button-group">
                  <Link to="/enneagram">
                    <button className="form-icon-save">
                      <PiPencilSimpleLineBold /> Responder
                    </button>
                  </Link>
                  <Link to="/perfil/enneagram-results">
                    <button className="form-icon-list">
                      <FiEye /> Ver Resultado
                    </button>
                  </Link>
                </div>
              </div>
            )}

            {/* ── Alterar Senha ── */}
            <div className="profile-section">
              <h3>Alterar Senha</h3>

              <div className="form-row first">
                <label htmlFor="current-password">Senha Atual:</label>
                <input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <label className="label-new-password" htmlFor="new-password">
                  Nova Senha:
                </label>
                <input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <label
                  className="label-new-password"
                  htmlFor="confirm-password"
                >
                  Confirmar Senha:
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="form-input"
                />
              </div>
              <button className="form-button" onClick={handleChangePassword}>
                Alterar Senha
              </button>
            </div>
          </div>
        )}

        {/* ─────────────────────────────────────────────────────────────────── */}
        {/* TAB: Meus Certificados                                              */}
        {/* ─────────────────────────────────────────────────────────────────── */}
        {activeTab === "certificates" && (
          <div className="profile-tab-content">
            {/* Certificados de outras organizações, cadastrados pelo usuário */}
            <CertificadosExternos />

            {/* Certificados emitidos pelos cursos do próprio Painel */}
            <CertificadosCursos />
          </div>
        )}
      </div>

      <Footer />

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmRemoveAvatar}
        title="Remover Foto de Perfil"
        message="Tem certeza que deseja remover sua foto de perfil?"
      />

      <AvatarModal
        isOpen={isAvatarModalOpen}
        onClose={() => setIsAvatarModalOpen(false)}
      />
    </div>
  );
};

export default Perfil;
